import clsx from "clsx";
import { useId, useRef, useState, type DragEvent, type ReactNode } from "react";
import { Button } from "../Button/Button";
import { Icon } from "../Icon/Icon";
import styles from "./FileDrop.module.css";

export interface FileDropProps {
  /** What to drop: "Supplier order (CSV or Excel)". */
  label: string;
  description?: ReactNode;
  /** Same syntax as <input accept>: ".csv,.xlsx,application/pdf". Dropped files are filtered too. */
  accept?: string;
  multiple?: boolean;
  disabled?: boolean;
  onFiles: (files: File[]) => void;
  error?: string | null;
  buttonLabel?: string;
  className?: string;
}

/** True when the file matches an `accept` list (extensions, exact MIME types or type/*). */
export function fileMatchesAccept(file: File, accept: string | undefined): boolean {
  if (!accept) return true;
  const name = file.name.toLowerCase();
  const type = file.type.toLowerCase();
  return accept
    .split(",")
    .map((token) => token.trim().toLowerCase())
    .filter(Boolean)
    .some((token) => {
      if (token.startsWith(".")) return name.endsWith(token);
      if (token.endsWith("/*")) return type.startsWith(token.slice(0, -1));
      return type === token;
    });
}

/** A drop zone with a "Choose file…" button, so it works with the keyboard and on touch screens. */
export function FileDrop({
  label,
  description,
  accept,
  multiple = false,
  disabled,
  onFiles,
  error,
  buttonLabel,
  className,
}: FileDropProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [dragging, setDragging] = useState(false);
  const [rejected, setRejected] = useState<string | null>(null);
  const descriptionId = useId();

  function accepted(files: FileList | null): File[] {
    const list = Array.from(files ?? []);
    const matching = list.filter((file) => fileMatchesAccept(file, accept));
    const chosen = multiple ? matching : matching.slice(0, 1);
    setRejected(
      matching.length < list.length
        ? `${list.length - matching.length} file${list.length - matching.length === 1 ? " was" : "s were"} skipped: only ${accept} files are accepted.`
        : null,
    );
    return chosen;
  }

  function handleDrop(event: DragEvent<HTMLDivElement>) {
    event.preventDefault();
    setDragging(false);
    if (disabled) return;
    const files = accepted(event.dataTransfer.files);
    if (files.length) onFiles(files);
  }

  const message = error ?? rejected;

  return (
    <div
      className={clsx(styles.zone, className)}
      data-dragging={dragging || undefined}
      data-disabled={disabled || undefined}
      data-invalid={message ? "" : undefined}
      onDragOver={(event) => {
        event.preventDefault();
        if (!disabled) setDragging(true);
      }}
      onDragLeave={() => setDragging(false)}
      onDrop={handleDrop}
    >
      <span className={styles.icon}>
        <Icon name="upload" size="md" />
      </span>
      <div className={styles.text}>
        <p className={styles.label}>{label}</p>
        <p className={styles.hint} id={descriptionId}>
          {description ?? (multiple ? "Drop files here or choose them." : "Drop a file here or choose one.")}
        </p>
      </div>
      <Button onClick={() => inputRef.current?.click()} disabled={disabled} aria-describedby={descriptionId}>
        {buttonLabel ?? (multiple ? "Choose files…" : "Choose file…")}
      </Button>
      <input
        ref={inputRef}
        className={styles.input}
        type="file"
        accept={accept}
        multiple={multiple}
        disabled={disabled}
        tabIndex={-1}
        aria-hidden="true"
        onChange={(event) => {
          const files = accepted(event.currentTarget.files);
          event.currentTarget.value = "";
          if (files.length) onFiles(files);
        }}
      />
      {message ? (
        <p className={styles.error} role="alert">
          <Icon name="alertCircle" />
          {message}
        </p>
      ) : null}
    </div>
  );
}
